import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchOrganisations } from '../services/organisationService';
import React from 'react';
import Fuse from 'fuse.js';
import { useSearchState } from '../contexts/SearchStateContext';
import { preprocessQuery, getEnhancedFuseOptions } from '../utils/queryProcessor';

const RESULTS_PER_PAGE = 6; // Number of results to show per page
const USER_INPUT_KEY = 'zahoot_hasUserInput';
const PAGE_REFRESH_KEY = 'zahoot_last_refresh_time';

export const useSearchForm = (isMobileView, hasValidSavedState = false) => {
  // Use search state context
  const { 
    searchState, 
    saveSearchState, 
    saveAiSearchState,
    saveStandardSearchState,
    saveFuzzySearchCriteria,
    shouldAutoExecuteAiSearch,
    markAutoExecutionComplete
  } = useSearchState();
  
  // Use refs to track initialization status
  const initializedRef = React.useRef(false);
  const searchAttemptedRef = React.useRef(false);
  
  const [animationStarted, setAnimationStarted] = useState(false);
  const [contentAnimating, setContentAnimating] = useState(false);
  const [shiftLeftActive, setShiftLeftActive] = useState(hasValidSavedState);
  
  // Search results state
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleResults, setVisibleResults] = useState([]);
  const [hasUserInput, setHasUserInput] = useState(hasValidSavedState);
  
  // Fuzzy search state
  const [fuzzySearchEnabled, setFuzzySearchEnabled] = useState(false);
  const [fuzzySearchCriteria, setFuzzySearchCriteria] = useState({
    name: '',
    practiceAreas: [],
    organisationType: '',
    sraNumber: '',
    address: '',
    phone: '',
    email: ''
  });
  
  const navigate = useNavigate();

  // Smart re-execution function for preserved searches
  const executePreservedSearch = async () => {
    if (!searchState || !searchState.shouldAutoExecute) {
      return;
    }

    console.log('Executing preserved search of type:', searchState.searchType);
    setIsLoading(true);
    setShiftLeftActive(true);
    setHasUserInput(true);

    try {
      switch (searchState.searchType) {
        case 'standard':
          if (searchState.searchCriteria.service || searchState.searchCriteria.area) {
            await handleGlobalSearch(
              searchState.searchCriteria.service,
              searchState.searchCriteria.area,
              searchState.searchCriteria.specific
            );
          }
          break;
          
        case 'quick':
          if (searchState.fuzzySearchCriteria && searchState.fuzzySearchEnabled) {
            setFuzzySearchEnabled(true);
            setFuzzySearchCriteria(searchState.fuzzySearchCriteria);
            await handleFuzzySearch(searchState.fuzzySearchCriteria);
          }
          break;
          
        case 'ai':
          // For AI searches, check if we should auto-execute based on credits/timing
          if (shouldAutoExecuteAiSearch()) {
            // This would need to be passed from parent component
            // We'll handle this in UserPage.js since it has access to handleAISearch
            console.log('AI search ready for auto-execution');
          } else {
            console.log('AI search requires user confirmation due to age/credits');
          }
          break;
          
        default:
          console.log('Unknown search type:', searchState.searchType);
      }
    } catch (error) {
      console.error('Error executing preserved search:', error);
    } finally {
      setIsLoading(false);
      markAutoExecutionComplete();
    }
  };

  // Start animations when component mounts
  useEffect(() => {
    // Run this only once to avoid infinite loops
    if (!initializedRef.current) {
      console.log('useSearchForm initialized with hasValidSavedState:', hasValidSavedState);
      initializedRef.current = true;
      
      setAnimationStarted(true);
      setContentAnimating(true);
      
      // If we have valid saved state, synchronize UI state
      if (hasValidSavedState) {
        setShiftLeftActive(true);
        setHasUserInput(true);
        
        // If there's search state with results but our results array is empty,
        // we need to preserve the total results count for proper rendering
        if (searchState && searchState.totalResults > 0 && !searchResults.length) {
          console.log('Restoring total results count:', searchState.totalResults);
          setTotalResults(searchState.totalResults);
        }

        // Execute preserved search if it should auto-execute (but not for AI searches - they're handled in UserPage)
        if (searchState && searchState.shouldAutoExecute && searchState.searchType !== 'ai') {
          setTimeout(() => {
            executePreservedSearch();
          }, 100);
        }
      } else {
        // Only check localStorage if we don't already have valid saved state
        const hasInputData = localStorage.getItem(USER_INPUT_KEY) === 'true';
        setHasUserInput(hasInputData);
      }
      
      // Check if this is a page refresh
      const checkIfRefresh = () => {
        const lastRefreshTime = sessionStorage.getItem(PAGE_REFRESH_KEY);
        const currentTime = Date.now();
        sessionStorage.setItem(PAGE_REFRESH_KEY, currentTime.toString());
        
        // If last refresh was less than 2 seconds ago, this is likely a refresh
        if (lastRefreshTime && (currentTime - parseInt(lastRefreshTime)) < 2000) {
          console.log('Page refresh detected in useSearchForm');
          return true;
        }
        return false;
      };
      
      checkIfRefresh();
    }
  }, [hasValidSavedState, searchState, searchResults.length]);

  // Handle standard search for results
  const handleGlobalSearch = async (selectedService, selectedArea, selectedBudget) => {
    console.log('Global search triggered with:', { 
      service: selectedService?.label, 
      area: selectedArea?.label, 
      budget: selectedBudget?.label 
    });
    
    // Mark that a search has been attempted
    searchAttemptedRef.current = true;
    
    // Safety check for undefined parameters
    if (!selectedService && !selectedArea && !selectedBudget) {
      console.warn('Global search triggered with no parameters, but continuing');
    }
    
    // Ensure user input state persists
    localStorage.setItem(USER_INPUT_KEY, 'true');
    
    // Update URL parameters to reflect the search
    const params = new URLSearchParams(window.location.search);
    
    // Preserve AI search parameters if they exist
    const existingAi = params.get('ai');
    const existingQuery = params.get('q');
    
    if (selectedService?.label) {
      params.set('area', selectedService.label);
    } else {
      params.delete('area');
    }
    
    if (selectedArea?.label) {
      params.set('city', selectedArea.label);
    } else {
      params.delete('city');
    }
    
    if (selectedBudget?.label) {
      params.set('budget', selectedBudget.label);
    } else {
      params.delete('budget');
    }
    
    // Restore AI search parameters if they were present
    if (existingAi) {
      params.set('ai', existingAi);
    }
    if (existingQuery) {
      params.set('q', existingQuery);
    }
    
    // Update URL without reloading the page
    const newRelativePathQuery = window.location.pathname + '?' + params.toString();
    window.history.pushState(null, '', newRelativePathQuery);
    
    // Always show the search results
    setShiftLeftActive(true);
    
    // Reset pagination when new search is performed
    setCurrentPage(1);
    setVisibleResults([]);
    
    // Start loading state
    setIsLoading(true);
    
    try {
      // Fetch real organisations data
      const organisations = await fetchOrganisations();
      console.log('Fetched organisations:', organisations.length);
      
      // Filter based on selected service and area with more flexible matching
      let filtered = [...organisations];
      
      if (selectedService?.label) {
        console.log('Filtering by service:', selectedService.label);
        // Strict service matching - check for exact practice area matches
        filtered = filtered.filter(org => {
          // Handle case where practice_areas might be missing
          if (!org.practice_areas || org.practice_areas.length === 0) {
            return selectedService.label.toLowerCase() === 'general';
          }
          
          // Handle case where practice_areas might be a string
          let areas = org.practice_areas;
          if (typeof areas === 'string') {
            try {
              areas = JSON.parse(areas);
            } catch (e) {
              console.error('Error parsing practice areas', e);
              areas = [];
            }
          }
          
          const matches = Array.isArray(areas) && areas.some(pa => {
            const areaName = typeof pa === 'object' ? pa.name : pa;
            const match = areaName.toLowerCase() === selectedService.label.toLowerCase();
            return match;
          });
          
          return matches;
        });
        console.log('After service filter, remaining:', filtered.length);
      }
      
      if (selectedArea?.label) {
        console.log('Filtering by area:', selectedArea.label);
        filtered = filtered.filter(org => {
          if (!org.offices || org.offices.length === 0) {
            return false;
          }
          
          const matches = org.offices.some(office => {
            const officeTown = office.town?.toLowerCase().trim();
            const selectedTown = selectedArea.label?.toLowerCase().trim();
            
            // Check exact match or London variations normalization
            return officeTown === selectedTown || 
                   (selectedTown === 'london' && officeTown?.includes('london')) ||
                   (officeTown === 'london' && selectedTown?.includes('london'));
          });
          return matches;
        });
        console.log('After area filter, remaining:', filtered.length);
      }
      
      // Update state with filtered results
      console.log('Final results:', filtered.length);
      setSearchResults(filtered);
      setTotalResults(filtered.length);
      
      // Save search state for back navigation and refresh
      saveStandardSearchState(selectedService, selectedArea, selectedBudget);
      
      // Initialize visible results with first page
      setVisibleResults(filtered.slice(0, RESULTS_PER_PAGE));
    } catch (error) {
      console.error('Error fetching search results:', error);
      setSearchResults([]);
      setTotalResults(0);
      setVisibleResults([]);
    } finally {
      setIsLoading(false);
    }
    
    // Close any open dropdowns by simulating a click outside
    document.dispatchEvent(new MouseEvent('mousedown', {
      bubbles: true,
      cancelable: true,
      view: window
    }));
    
    // Force scroll to search results on mobile
    if (isMobileView) {
      scrollToResults();
    }
  };
  
  // Handle fuzzy search
  const handleFuzzySearch = async (criteria) => {
    console.log('Fuzzy search triggered with criteria:', criteria);
    
    // Mark that a search has been attempted
    searchAttemptedRef.current = true;
    
    // Ensure user input state persists
    localStorage.setItem(USER_INPUT_KEY, 'true');
    
    // Always show the search results
    setShiftLeftActive(true);
    
    // Reset pagination when new search is performed
    setCurrentPage(1);
    setVisibleResults([]);
    setFuzzySearchCriteria(criteria);
    
    // Start loading state
    setIsLoading(true);
    
    try {
      // Fetch all organisations
      const organisations = await fetchOrganisations();
      console.log('Fetched organisations for fuzzy search:', organisations.length);
      
      // Use enhanced Fuse.js configuration with better scoring
      const fuseOptions = getEnhancedFuseOptions();
      
      // Create Fuse instance for fuzzy searching
      const fuse = new Fuse(organisations, fuseOptions);
      
      // Preprocess query for enhanced search terms
      let enhancedCriteria = { ...criteria };
      
      // If there's a name field, preprocess it for enhanced terms
      if (criteria.name) {
        const processed = preprocessQuery(criteria.name);
        console.log('Preprocessed query:', processed);
        
        // If we detected practice areas from the name, add them to practice areas
        if (processed.practiceAreas.length > 0 && !criteria.practiceAreas) {
          const primaryArea = processed.practiceAreas[0];
          const practiceAreaMap = {
            'divorce': 'Family / Matrimonial',
            'family': 'Family / Matrimonial',
            'property': 'Property - Residential',
            'conveyancing': 'Property - Residential',
            'immigration': 'Immigration',
            'criminal': 'Criminal',
            'employment': 'Employment',
            'personal injury': 'Personal Injury',
            'commercial': 'Commercial',
            'litigation': 'Litigation',
            'probate': 'Probate and Estate Administration'
          };
          enhancedCriteria.practiceAreas = practiceAreaMap[primaryArea.area] || primaryArea.area;
        }
        
        // If we detected location from name, add it to address
        if (processed.location && !criteria.address) {
          enhancedCriteria.address = processed.location;
        }
      }
      
      // Get all non-empty search criteria
      const searchTerms = [];
      
      // Check each criteria and add only if not empty
      if (enhancedCriteria.name) {
        // Create enhanced search terms for the name field
        const nameSearchTerms = [enhancedCriteria.name];
        
        // If we preprocessed the name, add enhanced terms
        if (criteria.name) {
          const processed = preprocessQuery(criteria.name);
          nameSearchTerms.push(...processed.enhancedTerms);
        }
        
        // Search across multiple name variations
        const nameQueries = nameSearchTerms.map(term => ({
          $path: ['practice_name'], 
          $val: term
        }));
        
        if (nameQueries.length > 1) {
          searchTerms.push({ $or: nameQueries });
        } else {
          searchTerms.push(nameQueries[0]);
        }
      }
      
      if (enhancedCriteria.practiceAreas && enhancedCriteria.practiceAreas) {
        // Check if practiceAreas is an array or string
        if (typeof enhancedCriteria.practiceAreas === 'string' && enhancedCriteria.practiceAreas.trim() !== '') {
          // If it's a string, search for that string and synonyms
          const practiceAreaTerms = [enhancedCriteria.practiceAreas];
          
          // Add synonyms if we can map the practice area
          const lowerArea = enhancedCriteria.practiceAreas.toLowerCase();
          if (lowerArea.includes('family') || lowerArea.includes('matrimonial')) {
            practiceAreaTerms.push('Family / Matrimonial', 'Family Law', 'Matrimonial', 'Divorce');
          } else if (lowerArea.includes('property') || lowerArea.includes('residential')) {
            practiceAreaTerms.push('Property - Residential', 'Conveyancing', 'Property Law');
          } else if (lowerArea.includes('immigration')) {
            practiceAreaTerms.push('Immigration', 'Visa', 'Asylum');
          } else if (lowerArea.includes('criminal')) {
            practiceAreaTerms.push('Criminal', 'Criminal Law', 'Defence');
          } else if (lowerArea.includes('employment')) {
            practiceAreaTerms.push('Employment', 'Workplace', 'Tribunal');
          }
          
          const practiceAreaQueries = practiceAreaTerms.map(term => ({
            $path: ['practice_areas.name'],
            $val: term
          }));
          
          if (practiceAreaQueries.length > 1) {
            searchTerms.push({ $or: practiceAreaQueries });
          } else {
            searchTerms.push(practiceAreaQueries[0]);
          }
        } else if (Array.isArray(enhancedCriteria.practiceAreas) && enhancedCriteria.practiceAreas.length > 0) {
          // If it's an array, search for any of the selected practice areas
          const practiceAreaQuery = enhancedCriteria.practiceAreas.map(area => ({
            $path: ['practice_areas.name'],
            $val: area
          }));
          
          if (practiceAreaQuery.length > 0) {
            searchTerms.push({ $or: practiceAreaQuery });
          }
        }
      }
      
      if (enhancedCriteria.organisationType) {
        searchTerms.push({ $path: ['organisation_type'], $val: enhancedCriteria.organisationType });
      }
      
      if (enhancedCriteria.sraNumber) {
        // Use exact matching for SRA numbers with the = prefix for precise search
        searchTerms.push({ $path: ['sra_number'], $val: `="${enhancedCriteria.sraNumber}"` });
      }
      
      if (enhancedCriteria.address) {
        // Search address across all office fields with location enhancement
        const addressTerms = [enhancedCriteria.address];
        
        // Add location variations for common cities
        const lowerAddress = enhancedCriteria.address.toLowerCase();
        if (lowerAddress === 'london') {
          addressTerms.push('London', 'City of London', 'Greater London');
        } else if (lowerAddress === 'manchester') {
          addressTerms.push('Manchester', 'Greater Manchester');
        } else if (lowerAddress === 'birmingham') {
          addressTerms.push('Birmingham', 'West Midlands');
        }
        
        const addressQueries = [];
        addressTerms.forEach(term => {
          addressQueries.push(
            { $path: ['offices.address1'], $val: term },
            { $path: ['offices.town'], $val: term },
            { $path: ['offices.postcode'], $val: term }
          );
        });
        
        searchTerms.push({ $or: addressQueries });
      }
      
      if (enhancedCriteria.phone) {
        // Search phone across main phone and office phone numbers
        const phoneQuery = [
          { $path: ['phone'], $val: enhancedCriteria.phone },
          { $path: ['offices.phone_number'], $val: enhancedCriteria.phone }
        ];
        searchTerms.push({ $or: phoneQuery });
      }
      
      if (enhancedCriteria.email) {
        // Search email across main email and office emails
        const emailQuery = [
          { $path: ['email'], $val: enhancedCriteria.email },
          { $path: ['offices.email'], $val: enhancedCriteria.email }
        ];
        searchTerms.push({ $or: emailQuery });
      }
      
      // Perform fuzzy search
      let results = [];
      
      // If no criteria specified, show all results
      if (searchTerms.length === 0) {
        results = organisations;
      } else {
        // Build the search pattern based on multiple fields
        const searchPattern = { $and: searchTerms };
        
        // Perform the search
        const fuseResults = fuse.search(searchPattern);
        console.log('Fuse search results:', fuseResults.length);
        
        // Extract just the items
        results = fuseResults.map(result => result.item);
      }
      
      console.log('Final fuzzy search results:', results.length);
      
      // Update state with results
      setSearchResults(results);
      setTotalResults(results.length);
      
      // Save fuzzy search state for back navigation and refresh
      saveFuzzySearchCriteria(criteria, true);
      
      // Initialize visible results with first page
      setVisibleResults(results.slice(0, RESULTS_PER_PAGE));
    } catch (error) {
      console.error('Error fetching fuzzy search results:', error);
      setSearchResults([]);
      setTotalResults(0);
      setVisibleResults([]);
    } finally {
      setIsLoading(false);
    }
    
    // Force scroll to search results on mobile
    if (isMobileView) {
      scrollToResults();
    }
  };
  
  // Helper function to scroll to results on mobile
  const scrollToResults = () => {
    // First ensure the document is scrollable
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.body.style.position = 'static';
    document.body.style.height = 'auto';
    document.documentElement.style.height = 'auto';
    
    // iOS specific fixes
    document.body.style.webkitOverflowScrolling = 'touch';
    document.documentElement.style.webkitOverflowScrolling = 'touch';
    
    setTimeout(() => {
      // Find the search results container
      const searchResultsContainer = document.querySelector('.user-page__search-results-container');
      if (searchResultsContainer) {
        // Enable smooth scrolling temporarily
        document.documentElement.style.scrollBehavior = 'smooth';
        
        // Try multiple approaches to scroll
        try {
          // Try scrollIntoView first
          searchResultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Also try window scroll as backup
          const rect = searchResultsContainer.getBoundingClientRect();
          const scrollPosition = window.pageYOffset + rect.top - 80; // Reduced offset for better positioning
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
          
          // For iOS Safari - focus on element to ensure it's in view
          searchResultsContainer.setAttribute('tabindex', '-1');
          searchResultsContainer.focus();
        } catch (error) {
          console.error('Error scrolling to results:', error);
          
          // Fallback scrolling approach
          window.scrollTo(0, document.body.scrollHeight / 2);
        }
        
        // Reset scroll behavior after a delay
        setTimeout(() => {
          document.documentElement.style.scrollBehavior = '';
        }, 1000);
      }
    }, 800); // Increased timeout to ensure content is rendered
  };

  // Function to handle loading more results
  const handleLoadMore = () => {
    console.log('Loading more results...');
    const nextPage = currentPage + 1;
    const endIndex = nextPage * RESULTS_PER_PAGE;
    const newVisibleResults = [...visibleResults, ...searchResults.slice((nextPage - 1) * RESULTS_PER_PAGE, endIndex)];
    
    setCurrentPage(nextPage);
    setVisibleResults(newVisibleResults);
    
    console.log('Current page:', nextPage);
    console.log('Visible results:', newVisibleResults.length);
    console.log('Total results:', searchResults.length);
  };

  // Function to toggle fuzzy search mode
  const toggleFuzzySearch = (isEnabled) => {
    setFuzzySearchEnabled(isEnabled);
  };

  return {
    animationStarted,
    contentAnimating,
    shiftLeftActive,
    setShiftLeftActive,
    searchResults,
    setSearchResults,
    isLoading,
    setIsLoading,
    totalResults,
    setTotalResults,
    currentPage,
    setCurrentPage,
    visibleResults,
    setVisibleResults,
    hasUserInput,
    setHasUserInput,
    handleGlobalSearch,
    handleLoadMore,
    fuzzySearchEnabled,
    toggleFuzzySearch,
    fuzzySearchCriteria,
    setFuzzySearchCriteria,
    handleFuzzySearch
  };
}; 